import React, {
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import { BrandColor } from '@styleseat/brand';
import { Box, BoxSizeOptions } from '@styleseat/ui';
import { Stack } from '../../../../ui';
import { LegalModal, LegalDoc } from '../../../../shared/legal/LegalModal';
import MultiFactorAuthFormOverLay from './MultiFactorAuthFormOverlay';
import { MultiFactorAuthChoiceType, MultiFactorAuthMethod } from '../../../../shared/login/MultiFactorAuthForm/types';
import { LoginErrorBanner } from './LoginErrorBanner';
import MultiFactorAuthForm from '../../../../shared/login/MultiFactorAuthForm';
import { InitialEmailState } from './InlineSigninAndSignupStates/InitialEmailState';
import { SigninState } from './InlineSigninAndSignupStates/SigninState';
import { SignupState } from './InlineSigninAndSignupStates/SignupState';
import { NewUnderlineLink } from './NewUnderlineLink';
import type { AppleSSOResponse, SetCurrentUserPayload } from '../../../../../store/CurrentUser.types';
import { useMount, useOnce } from '../../../../../hooks';
import { AnalyticsEvents, EmailChangeEvent } from './contants';
import { sendAnalytics } from './utils';

export enum LoginStateOptions {
  'Initial' = 'initial',
  'Login' = 'login',
  'SignUp' = 'signup',
  'MFA' = 'mfa',
}

type InlineSigninAndSignupProps = {
  // Components
  renderHeader?: () => React.ReactNode;
  paddingTop?: BoxSizeOptions;
  paddingBottom?: BoxSizeOptions;
  paddingX?: BoxSizeOptions;
  useInlineMfa?: boolean;
  // Form fields
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  phoneNumber: string;
  // Form update functions
  onEmailChange: (event: EmailChangeEvent) => void;
  onFirstNameChange: (string) => void;
  onLastNameChange: (string) => void;
  onPasswordChange: (string) => void;
  onPhoneNumberChange: (string) => void;
  // MFA
  mfaChoices: MultiFactorAuthChoiceType[];
  mfaMethod: MultiFactorAuthMethod;
  onCloseMfa: () => void;
  // General state
  canDisplaySSO?: boolean;
  componentState: LoginStateOptions;
  isLoading: boolean;
  isLoadingPasswordReset: boolean;
  showForgotPasswordSuccess: boolean;
  showLegalModal: boolean;
  legalDoc: LegalDoc;
  hasCheckedEmail: boolean;
  setShowLegalModal: Dispatch<SetStateAction<boolean>>;
  setLegalDoc: Dispatch<SetStateAction<LegalDoc>>;
  // Helper functions
  onSignInUser: (
    mfaCode: string,
    currentMfaMethod: MultiFactorAuthMethod,
    isResend?: boolean
  ) => Promise<void>;
  onSignUpUser: () => Promise<void>;
  onCheckIfUserExists: (emailValue?: string) => Promise<void>;
  onForgotPassword: (emailToResetPassword: string) => Promise<void>;
  onContinueAsGuestClick: () => void;
  onFacebookSignup: () => Promise<SetCurrentUserPayload>;
  onAppleSignup: (mode: 'signup' | 'login') => Promise<AppleSSOResponse>;
  onAccountCreated: (success: boolean, userData?: any) => void;
  // Errors
  userStateErrors: any;
  emailHasError: boolean;
  showPasswordError: boolean;
  hasPhoneNumberError: boolean;
  hasEmailCheckingError: boolean;
};

export const InlineSigninAndSignup: React.FCWithChildren<InlineSigninAndSignupProps> = ({
  renderHeader = () => null,
  paddingTop = 20,
  paddingBottom = 20,
  paddingX = 0,
  useInlineMfa = false,
  email,
  firstName,
  lastName,
  password,
  phoneNumber,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onPhoneNumberChange,
  mfaChoices,
  mfaMethod,
  onCloseMfa,
  canDisplaySSO = false,
  componentState,
  isLoading,
  isLoadingPasswordReset,
  showForgotPasswordSuccess,
  showLegalModal,
  legalDoc,
  hasPhoneNumberError,
  hasCheckedEmail,
  hasEmailCheckingError,
  setShowLegalModal,
  setLegalDoc,
  onSignInUser,
  onSignUpUser,
  onCheckIfUserExists,
  onForgotPassword,
  onContinueAsGuestClick,
  onFacebookSignup,
  onAppleSignup,
  onAccountCreated,
  userStateErrors,
  emailHasError,
  showPasswordError,
}) => {
  const [showNewMfaCodeSent, setShowNewMfaCodeSent] = useState<boolean>(false);
  const handleEmailInputChange = ({ newValue, validate = true }: EmailChangeEvent) => {
    onEmailChange({ newValue, validate });
  };
  const handleFirstNameInputChange = (newValue: string) => {
    onFirstNameChange(newValue);
  };
  const handleLastNameInputChange = (newValue: string) => {
    onLastNameChange(newValue);
  };
  const handlePasswordInputChange = (newValue: string) => {
    onPasswordChange(newValue);
  };
  const handlePhoneNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPhoneNumberChange(e.target.value);
  };
  const handleContinueClicked = (emailValue?: string) => {
    if (!emailValue) {
      sendAnalytics(AnalyticsEvents.ContinueClick);
    }
    return onCheckIfUserExists(emailValue);
  };
  const handleSignInClicked = () => {
    sendAnalytics(AnalyticsEvents.LoginClick);
    return onSignInUser(undefined, mfaMethod);
  };
  const handleSignInWithMfaClicked = (mfaCode: string) => {
    sendAnalytics(AnalyticsEvents.LoginClick);
    return onSignInUser(mfaCode, mfaMethod);
  };
  const handleOnRequestMfaCode = async (
    mfaMethodValue: MultiFactorAuthMethod,
    notify?: boolean,
    isResend?: boolean,
  ) => {
    sendAnalytics(AnalyticsEvents.MfaRequested);
    await onSignInUser(undefined, mfaMethodValue, isResend);
    if (isResend) {
      setShowNewMfaCodeSent(true);
      setTimeout(() => {
        setShowNewMfaCodeSent(false);
      }, 15000);
    }
  };
  const handleSignUpClicked = () => {
    sendAnalytics(AnalyticsEvents.SignupClick);
    return onSignUpUser();
  };
  const handleForgotPasswordSubmit = (emailToResetPassword: string) => {
    sendAnalytics(AnalyticsEvents.ForgotPassword);
    onForgotPassword(emailToResetPassword);
  };
  const handleContinueAsGuestClicked = () => onContinueAsGuestClick();
  const handleOpenTOS = () => {
    setLegalDoc(LegalDoc.ConsumerTermsOfService);
    setShowLegalModal(true);
  };
  const handleOpenPrivacyPolicy = () => {
    setLegalDoc(LegalDoc.PrivacyPolicyInfo);
    setShowLegalModal(true);
  };
  const handleCloseTOSOrPrivacyPolicy = () => {
    setShowLegalModal(false);
    setLegalDoc(null);
  };

  useMount(() => {
    sendAnalytics(AnalyticsEvents.Landed);
  });
  useOnce(() => {
    sendAnalytics(AnalyticsEvents.MfaTriggered);
  }, componentState === LoginStateOptions.MFA || null);

  // Conditional render constants
  const passwordHasError = userStateErrors?.password;
  const shouldShowMfa = componentState === LoginStateOptions.MFA;
  const shouldShowInlineMfa = shouldShowMfa && useInlineMfa;
  const shouldShowContinueButton = componentState === LoginStateOptions.Initial
    && !shouldShowInlineMfa;
  const shouldShowSignInFields = componentState === LoginStateOptions.Login
    && !shouldShowInlineMfa;
  const shouldShowSignUpFields = componentState === LoginStateOptions.SignUp
    && !shouldShowInlineMfa;
  const shouldDisableContinueButton = (
    !email
    || emailHasError
  );
  const shouldDisableSignInButton = (
    !email
    || !password
    || emailHasError
    || showPasswordError
  );
  const shouldDisableSignUpButton = (
    !email
    || !password
    || !phoneNumber
    || !firstName
    || !lastName
    || hasPhoneNumberError
  );

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      if (componentState === LoginStateOptions.Initial && !shouldDisableContinueButton) {
        handleContinueClicked();
      }
      if (componentState === LoginStateOptions.Login && !shouldDisableSignInButton) {
        handleSignInClicked();
      }
      if (componentState === LoginStateOptions.SignUp && !shouldDisableSignUpButton) {
        handleSignUpClicked();
      }
    }
  };

  // eslint-disable-next-line no-underscore-dangle
  const hasSignInOrSignUpErrors = userStateErrors?.__all__ && hasCheckedEmail;
  const showTooManyAttemptsMessage = userStateErrors?.errorCode;
  const emailError = (hasEmailCheckingError ? 'We\'re having problems connecting to our services. Please try again later' : null)
  || (emailHasError ? 'Invalid Email' : null);
  // eslint-disable-next-line no-nested-ternary
  const passwordErrorMessage = passwordHasError
    ? 'Invalid Password'
    : typeof userStateErrors?.password === 'string'
      ? null
      : userStateErrors?.password?.[0];

  const renderErrors = () => {
    if (hasSignInOrSignUpErrors) {
      return (
        // eslint-disable-next-line no-underscore-dangle
        <LoginErrorBanner errorText={userStateErrors.__all__} />
      );
    }

    if (showTooManyAttemptsMessage) {
      return (
        <LoginErrorBanner errorText={userStateErrors.errorMessage} />
      );
    }

    return null;
  };

  return (
    <React.Fragment>
      <Stack
        space={16}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        paddingX={paddingX}
      >
        {renderHeader()}
        {(shouldShowMfa && !shouldShowInlineMfa) && (
          <MultiFactorAuthFormOverLay
            mfaChoices={mfaChoices}
            mfaMethod={mfaMethod}
            isLoading={isLoading}
            userStateErrors={userStateErrors}
            handleSignInUser={onSignInUser}
            onClose={onCloseMfa}
          />
        )}
        {(shouldShowInlineMfa) && (
          <MultiFactorAuthForm
            mfaChoices={mfaChoices}
            mfaMethod={mfaMethod}
            isSubmitting={isLoading}
            errors={userStateErrors}
            onRequestCode={handleOnRequestMfaCode}
            showNewCodeSent={showNewMfaCodeSent}
            onVerify={handleSignInWithMfaClicked}
          />
        )}
        {shouldShowContinueButton && (
          <InitialEmailState
            shouldAutoFocusOnEmail={false}
            canDisplaySSO={canDisplaySSO}
            emailValue={email}
            passwordValue={password}
            errorMessage={emailError}
            onChangeEmail={handleEmailInputChange}
            onChangePassword={handlePasswordInputChange}
            onClickContinue={handleContinueClicked}
            onKeyPress={handleKeyPress}
            onEmailFocus={() => sendAnalytics(AnalyticsEvents.Email)}
            onFacebookSignup={onFacebookSignup}
            onAppleSignup={onAppleSignup}
            isLoading={isLoading}
            isContinueDisabled={shouldDisableContinueButton}
            onOpenTOS={handleOpenTOS}
            onOpenPrivacyPolicy={handleOpenPrivacyPolicy}
            onAccountCreated={onAccountCreated}
          />
        )}
        {shouldShowSignInFields && (
          <SigninState
            emailErrorMessage={emailError}
            emailValue={email}
            onChangeEmail={handleEmailInputChange}
            onEmailFocus={() => sendAnalytics(AnalyticsEvents.Email)}
            onKeyPress={handleKeyPress}
            passwordValue={password}
            onChangePassword={handlePasswordInputChange}
            onPasswordFocus={() => sendAnalytics(AnalyticsEvents.PasswordLogin)}
            errorsComponent={renderErrors()}
            isLoading={isLoading}
            onClickSignIn={handleSignInClicked}
            isDisabled={shouldDisableSignInButton}
            showForgotPasswordSuccess={showForgotPasswordSuccess}
            isLoadingPasswordReset={isLoadingPasswordReset}
            onClickForgotPassword={() => handleForgotPasswordSubmit(email)}
          />
        )}
        {shouldShowSignUpFields && (
          <SignupState
            emailValue={email}
            passwordValue={password}
            firstNameValue={firstName}
            lastNameValue={lastName}
            phoneNumberValue={phoneNumber}
            isLoading={isLoading}
            isSignupDisabled={shouldDisableSignUpButton}
            onChangeEmail={handleEmailInputChange}
            onChangePassword={handlePasswordInputChange}
            onChangeFirstName={handleFirstNameInputChange}
            onChangeLastName={handleLastNameInputChange}
            onChangePhoneNumber={handlePhoneNumberInputChange}
            onClickSignup={handleSignUpClicked}
            onKeyPress={handleKeyPress}
            onEmailFocus={() => sendAnalytics(AnalyticsEvents.Email)}
            onPasswordFocus={() => sendAnalytics(AnalyticsEvents.PasswordSignUp)}
            onFirstNameFocus={() => sendAnalytics(AnalyticsEvents.FirstName)}
            onLastNameFocus={() => sendAnalytics(AnalyticsEvents.LastName)}
            onPhoneNumberFocus={() => sendAnalytics(AnalyticsEvents.Phone)}
            emailErrorMessage={emailError}
            passwordErrorMessage={passwordErrorMessage}
            firstNameErrorMessage={userStateErrors?.first_name}
            lastNameErrorMessage={userStateErrors?.last_name}
            phoneNumberErrorMessage={userStateErrors?.phone_number}
            onOpenTOS={handleOpenTOS}
            onOpenPrivacyPolicy={handleOpenPrivacyPolicy}
          />
        )}
        {onContinueAsGuestClick && (
          <Box
            paddingTop={8}
            justifyContent="center"
            alignItems="center"
            maxWidth="100%"
            minWidth="100%"
          >
            <Box
              maxWidth={110}
              minWidth={110}
              testID="booking-sign-in-and-up-continue-as-guest"
            >
              <NewUnderlineLink color={BrandColor.Metal} onPress={handleContinueAsGuestClicked}>
                Continue As Guest
              </NewUnderlineLink>
            </Box>
          </Box>
        )}
      </Stack>
      {showLegalModal && <LegalModal doc={legalDoc} onDismiss={handleCloseTOSOrPrivacyPolicy} />}
    </React.Fragment>
  );
};
